import React from 'react';

import Box from '@mui/material/Box';

/**
 * The AI designer's replies are markdown: headings, paragraphs, lists (nested), tables, quotes,
 * fenced code, and **bold**, *italic*, ~~struck~~, `code` and [links](https://…) inside them.
 * Built as React elements, never as HTML, so the text needs no sanitizing. A reply still streaming
 * in renders too - an unclosed ** or ``` just shows as it is until its end arrives.
 * A `#RRGGBB` in code gets a swatch of its color.
 */

type TBlock =
    | { kind: 'heading'; level: number; text: string }
    | { kind: 'paragraph'; lines: string[] }
    | { kind: 'code'; text: string }
    | { kind: 'quote'; lines: string[] }
    | { kind: 'rule' }
    | { kind: 'list'; items: IListItem[]; ordered: boolean; start: number }
    | { kind: 'table'; head: string[]; rows: string[][] };

interface IListItem { lines: string[]; children: TBlock[] }

const FENCE = /^\s*(```|~~~)/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

const indentOf = (line: string) => line.replace(/\t/g, '    ').match(/^ */)![0].length;
const isBlank = (line: string) => !line.trim();

function tableCells(line: string): string[] {
    return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

/** Starts a block other than a paragraph - ends the paragraph before it. */
function startsBlock(lines: string[], i: number): boolean {
    const line = lines[i];
    return FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || LIST_ITEM.test(line)
        || (line.includes('|') && i + 1 < lines.length && TABLE_SEPARATOR.test(lines[i + 1]) && lines[i + 1].includes('-'));
}

function parseBlocks(lines: string[]): TBlock[] {
    const blocks: TBlock[] = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (isBlank(line)) { i++; continue; }

        const fence = line.match(FENCE);
        if (fence) {
            const body: string[] = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith(fence[1])) body.push(lines[i++]);
            i++;
            blocks.push({ kind: 'code', text: body.join('\n') });
            continue;
        }
        const heading = line.match(HEADING);
        if (heading) {
            blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2] });
            i++;
            continue;
        }
        if (RULE.test(line)) {
            blocks.push({ kind: 'rule' });
            i++;
            continue;
        }
        if (QUOTE.test(line)) {
            const quoted: string[] = [];
            while (i < lines.length && QUOTE.test(lines[i])) quoted.push(lines[i++].match(QUOTE)![1]);
            blocks.push({ kind: 'quote', lines: quoted });
            continue;
        }
        if (line.includes('|') && i + 1 < lines.length && TABLE_SEPARATOR.test(lines[i + 1]) && lines[i + 1].includes('-')) {
            const head = tableCells(line);
            const rows: string[][] = [];
            i += 2;
            while (i < lines.length && lines[i].includes('|') && !isBlank(lines[i])) rows.push(tableCells(lines[i++]));
            blocks.push({ kind: 'table', head, rows });
            continue;
        }
        if (LIST_ITEM.test(line)) {
            // the list runs on through its items, their indented continuation lines and the blank lines between
            const listLines: string[] = [];
            const base = indentOf(line);
            while (i < lines.length) {
                const l = lines[i];
                if (isBlank(l)) {
                    let next = i + 1;
                    while (next < lines.length && isBlank(lines[next])) next++;
                    if (next < lines.length && (indentOf(lines[next]) > base || LIST_ITEM.test(lines[next]))) {
                        listLines.push(l);
                        i++;
                        continue;
                    }
                    break;
                }
                if (indentOf(l) < base && !LIST_ITEM.test(l)) break;
                if (indentOf(l) <= base && !LIST_ITEM.test(l) && listLines.length && isBlank(listLines[listLines.length - 1])) break;
                if (!LIST_ITEM.test(l) && indentOf(l) <= base && startsBlock(lines, i)) break;
                listLines.push(l);
                i++;
            }
            blocks.push(parseList(listLines));
            continue;
        }
        const paragraph: string[] = [];
        while (i < lines.length && !isBlank(lines[i]) && !startsBlock(lines, i)) paragraph.push(lines[i++].trim());
        blocks.push({ kind: 'paragraph', lines: paragraph });
    }
    return blocks;
}

/** Items at the first line's indent; anything indented deeper belongs to the item above it. */
function parseList(lines: string[]): TBlock {
    const first = lines[0].match(LIST_ITEM)!;
    const base = indentOf(lines[0]);
    const ordered = /\d/.test(first[2]);
    const items: { head: string[]; rest: string[] }[] = [];
    for (const line of lines) {
        const item = line.match(LIST_ITEM);
        if (item && indentOf(line) <= base) {
            items.push({ head: [item[3]], rest: [] });
        } else {
            const current = items[items.length - 1];
            if (!current.rest.length && !LIST_ITEM.test(line) && !isBlank(line)) current.head.push(line.trim());
            else current.rest.push(line);
        }
    }
    return {
        kind: 'list', ordered, start: ordered ? parseInt(first[2], 10) : 1,
        items: items.map(({ head, rest }) => ({ lines: head, children: parseBlocks(dedent(rest)) })),
    };
}

function dedent(lines: string[]): string[] {
    const indents = lines.filter((l) => !isBlank(l)).map(indentOf);
    const cut = indents.length ? Math.min(...indents) : 0;
    return lines.map((l) => l.replace(/\t/g, '    ').slice(cut));
}

const INLINE = /(`+)([\s\S]*?[^`])\1(?!`)|\*\*([\s\S]+?)\*\*|__([\s\S]+?)__|\*(?![\s*])([\s\S]*?[^\s*])\*|~~([\s\S]+?)~~|\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<)]+[^\s<).,;:!?'"])/g;
const SAFE_URL = /^(https?:|mailto:|\/|#)/i;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

function Swatch({ color }: { color: string }) {
    return <Box component="span" sx={{ display: 'inline-block', width: '0.9em', height: '0.9em', mr: 0.5,
        verticalAlign: '-0.1em', bgcolor: color, border: '1px solid rgba(0,0,0,0.25)', borderRadius: '2px' }} />;
}

function inline(text: string, keyPrefix = ''): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    let last = 0;
    let n = 0;
    for (const m of text.matchAll(INLINE)) {
        if (m.index! > last) out.push(text.slice(last, m.index));
        const key = `${keyPrefix}${n++}`;
        if (m[1]) {
            const code = m[2].trim();
            out.push(<Box component="code" key={key} sx={{ fontFamily: 'monospace', fontSize: '0.92em',
                bgcolor: 'rgba(0,0,0,0.06)', borderRadius: '3px', px: 0.4, py: 0.1, whiteSpace: 'nowrap' }}>
                {HEX_COLOR.test(code) && <Swatch color={code} />}{code}
            </Box>);
        } else if (m[3] || m[4]) {
            out.push(<strong key={key}>{inline(m[3] ?? m[4], `${key}.`)}</strong>);
        } else if (m[5]) {
            out.push(<em key={key}>{inline(m[5], `${key}.`)}</em>);
        } else if (m[6]) {
            out.push(<s key={key}>{inline(m[6], `${key}.`)}</s>);
        } else if (m[7]) {
            out.push(SAFE_URL.test(m[8])
                ? <a key={key} href={m[8]} target="_blank" rel="noopener noreferrer">{inline(m[7], `${key}.`)}</a>
                : m[0]);
        } else {
            out.push(<a key={key} href={m[9]} target="_blank" rel="noopener noreferrer">{m[9]}</a>);
        }
        last = m.index! + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

/** Lines of a paragraph keep their breaks - the designer writes them as lines, not as one flowing text. */
function inlineLines(lines: string[]): React.ReactNode[] {
    return lines.flatMap((line, i) => (i ? [<br key={`br${i}`} />, ...inline(line, `${i}:`)] : inline(line, `${i}:`)));
}

function renderBlocks(blocks: TBlock[]): React.ReactNode[] {
    return blocks.map((block, i) => {
        switch (block.kind) {
            case 'heading': {
                const Tag = `h${Math.min(6, block.level + 2)}` as 'h3';
                return <Tag key={i}>{inline(block.text)}</Tag>;
            }
            case 'paragraph':
                return <p key={i}>{inlineLines(block.lines)}</p>;
            case 'code':
                return <pre key={i}><code>{block.text}</code></pre>;
            case 'quote':
                return <blockquote key={i}>{renderBlocks(parseBlocks(block.lines))}</blockquote>;
            case 'rule':
                return <hr key={i} />;
            case 'list': {
                const items = block.items.map((item, j) => (
                    <li key={j}>{inlineLines(item.lines)}{renderBlocks(item.children)}</li>
                ));
                return block.ordered
                    ? <ol key={i} start={block.start}>{items}</ol>
                    : <ul key={i}>{items}</ul>;
            }
            case 'table':
                return (
                    <Box key={i} sx={{ overflowX: 'auto' }}>
                        <table>
                            <thead><tr>{block.head.map((c, j) => <th key={j}>{inline(c)}</th>)}</tr></thead>
                            <tbody>{block.rows.map((row, r) => (
                                <tr key={r}>{row.map((c, j) => <td key={j}>{inline(c)}</td>)}</tr>
                            ))}</tbody>
                        </table>
                    </Box>
                );
        }
    });
}

const markdownSx = {
    typography: 'body2',
    wordBreak: 'break-word',
    '& > :first-of-type': { mt: 0 },
    '& > :last-child': { mb: 0 },
    '& p': { my: 0.75 },
    '& h3, & h4, & h5, & h6': { fontSize: '1em', fontWeight: 700, mt: 1.5, mb: 0.5 },
    '& h3': { fontSize: '1.15em' },
    '& ul, & ol': { my: 0.5, pl: 2.5 },
    '& li': { my: 0.25 },
    '& li > ul, & li > ol': { my: 0.25 },
    '& li > p': { my: 0.25 },
    '& pre': { fontFamily: 'monospace', fontSize: 11, lineHeight: 1.45, whiteSpace: 'pre-wrap', bgcolor: '#f5f5f5',
        border: '1px solid #e0e0e0', borderRadius: 1, p: 1, my: 0.75, overflow: 'auto' },
    '& blockquote': { borderLeft: '3px solid rgba(0,0,0,0.2)', color: 'text.secondary', pl: 1.25, mx: 0, my: 0.75 },
    '& hr': { border: 0, borderTop: '1px solid rgba(0,0,0,0.12)', my: 1.25 },
    '& table': { borderCollapse: 'collapse', my: 0.75 },
    '& th, & td': { border: '1px solid #e0e0e0', px: 0.75, py: 0.4, textAlign: 'left', verticalAlign: 'top' },
    '& th': { bgcolor: '#f5f5f5', fontWeight: 600 },
    '& a': { color: 'primary.main' },
} as const;

export function Markdown({ text }: { text: string }) {
    const blocks = React.useMemo(() => parseBlocks(text.replace(/\r\n?/g, '\n').split('\n')), [text]);
    return <Box sx={markdownSx}>{renderBlocks(blocks)}</Box>;
}
